<script lang="ts">
	import * as m from '$lib/paraglide/messages';
	import { baseLocale, localizeHref, locales, type Locale } from '$lib/paraglide/runtime';
	import { page } from '$app/stores';
	import { IsMobile } from '$lib/hooks/is-mobile.svelte';
	import * as Sheet from '$lib/ui/primitives/sheet';
	import { toggleMode, mode } from 'mode-watcher';
	import { Menu, Moon, Sun } from '@lucide/svelte';
	import brandMarkDarkBackground from '$lib/assets/samlla-znak-kolor-ciemne-tlo.svg';
	import brandMarkLightBackground from '$lib/assets/samlla-znak-kolor-jasne-tlo.svg';
	import { wikiCategoryLabel } from '../../logic/category-labels';
	import type { WikiSidebarSection } from '../../logic/wiki-index';
	import type { WikiSearchRecord } from '../../logic/wiki-search';
	import { WikiSearch } from '../WikiSearch';

	// Switcher języka to realne anchory z hrefami z page map (natywne slugi!),
	// nie setLocale(). data-sveltekit-reload: zmiana języka = pełny load
	// statycznej strony, locale/messages/<html lang> zawsze spójne.
	const translations = $derived(
		($page.data.translations as Partial<Record<Locale, string>> | undefined) ?? {}
	);
	const currentLocale = $derived(($page.data.locale as Locale | undefined) ?? baseLocale);
	const sections = $derived(($page.data.sidebar as WikiSidebarSection[] | undefined) ?? []);
	const searchRecords = $derived(
		($page.data.searchRecords as WikiSearchRecord[] | undefined) ?? []
	);

	let mobileMenuOpen = $state(false);
	const isMobile = new IsMobile(861);

	$effect(() => {
		if (!isMobile.current) {
			mobileMenuOpen = false;
		}
	});

	function langHref(locale: Locale): string {
		return translations[locale] ?? (locale === baseLocale ? '/wiki' : `/${locale}/wiki`);
	}
</script>

<nav class="site-nav">
	<a href={localizeHref('/')} class="brand">
		<span class="brand-mark" aria-hidden="true">
			<img
				class="brand-logo brand-mark-light"
				src={brandMarkLightBackground}
				alt=""
				width="26"
				height="26"
			/>
			<img
				class="brand-logo brand-mark-dark"
				src={brandMarkDarkBackground}
				alt=""
				width="26"
				height="26"
			/>
		</span>
		<span>Samlla</span>
		<span class="wiki-tag">Wiki</span>
	</a>

	<div class="nav-cta">
		<div class="lang-links">
			{#each locales as locale (locale)}
				<a
					href={langHref(locale)}
					data-sveltekit-reload
					aria-current={currentLocale === locale ? 'page' : undefined}
				>
					{locale.toUpperCase()}
				</a>
			{/each}
		</div>

		<div class="desktop-nav-actions">
			<a class="changelog-link" href={localizeHref('/changelog')}>{m.wiki_changelog_link()}</a>
			<button
				class="theme-toggle"
				onclick={toggleMode}
				aria-label={m.landing_theme_toggle()}
				type="button"
			>
				{#if mode.current === 'dark'}<Sun size={16} />{:else}<Moon size={16} />{/if}
			</button>
			<a class="btn btn-primary btn-sm" href={localizeHref('/app/journal')}>
				{m.wiki_open_app()} <span class="arrow">→</span>
			</a>
		</div>

		<div class="mobile-nav-menu">
			<Sheet.Root bind:open={mobileMenuOpen}>
				<Sheet.Trigger
					class="inline-flex size-9 items-center justify-center rounded-full border border-[var(--v3-border)] bg-transparent text-[var(--v3-muted)] transition-colors hover:border-[var(--v3-border-strong)] hover:text-[var(--v3-text)]"
					aria-label={m.landing_mobile_menu_open()}
					type="button"
				>
					<Menu size={18} />
				</Sheet.Trigger>
				<Sheet.Content
					side="right"
					closeLabel={m.landing_mobile_menu_close()}
					class="w-[calc(100vw-1rem)] max-w-80 gap-0 overflow-y-auto border-[var(--v3-border)] bg-[var(--v3-bg)] text-[var(--v3-text)]"
				>
					<Sheet.Header class="border-b border-[var(--v3-border)] p-6 text-left">
						<Sheet.Title>{m.wiki_title()}</Sheet.Title>
						<Sheet.Description class="sr-only">
							{m.landing_mobile_menu_description()}
						</Sheet.Description>
					</Sheet.Header>

					<div class="flex flex-1 flex-col p-6">
						<div class="pb-5">
							<WikiSearch records={searchRecords} onNavigate={() => (mobileMenuOpen = false)} />
						</div>
						<div class="flex flex-col gap-5">
							{#each sections as section (section.category)}
								<div class="flex flex-col gap-1">
									<span
										class="px-3 text-xs font-bold tracking-wider text-[var(--v3-muted)] uppercase"
									>
										{wikiCategoryLabel(section.category)}
									</span>
									{#each section.items as item (item.pageId)}
										<a
											class="flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 font-medium transition-colors hover:bg-[var(--v3-surface)]"
											href={item.href}
											aria-current={$page.url.pathname === item.href ? 'page' : undefined}
											onclick={() => (mobileMenuOpen = false)}
										>
											<span>{item.title}</span>
											{#if item.fallback}
												<span
													class="rounded-full border border-[var(--v3-border)] px-2 text-[0.62rem] font-bold tracking-wide text-[var(--v3-muted)]"
												>
													EN
												</span>
											{/if}
										</a>
									{/each}
								</div>
							{/each}
						</div>

						<div class="mt-auto flex flex-col gap-3 pt-8">
							<a
								class="flex h-11 items-center gap-3 rounded-lg border border-[var(--v3-border)] px-4 font-medium transition-colors hover:border-[var(--v3-border-strong)]"
								href={localizeHref('/changelog')}
								onclick={() => (mobileMenuOpen = false)}
							>
								{m.wiki_changelog_link()}
							</a>
							<button
								class="flex h-11 items-center gap-3 rounded-lg border border-[var(--v3-border)] px-4 text-left font-medium transition-colors hover:border-[var(--v3-border-strong)]"
								onclick={toggleMode}
								aria-label={m.landing_theme_toggle()}
								type="button"
							>
								{#if mode.current === 'dark'}<Sun size={18} />{:else}<Moon size={18} />{/if}
								<span>{m.landing_theme_toggle()}</span>
							</button>
							<a
								class="btn btn-primary btn-sm justify-center"
								href={localizeHref('/app/journal')}
								onclick={() => (mobileMenuOpen = false)}
							>
								{m.wiki_open_app()} <span class="arrow">→</span>
							</a>
						</div>
					</div>
				</Sheet.Content>
			</Sheet.Root>
		</div>
	</div>
</nav>

<style>
	.wiki-tag {
		font-size: 0.72rem;
		font-weight: 700;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--v3-muted);
		border: 1px solid var(--v3-border);
		border-radius: 9999px;
		padding: 0.1rem 0.5rem;
		margin-left: 0.35rem;
	}
	.lang-links {
		display: inline-flex;
		gap: 0.25rem;
		margin-right: 0.25rem;
	}
	.lang-links a {
		font-size: 0.78rem;
		font-weight: 600;
		color: var(--v3-muted);
		padding: 0.25rem 0.4rem;
		border-radius: 0.5rem;
		text-decoration: none;
	}
	.lang-links a:hover {
		color: var(--v3-text);
	}
	.lang-links a[aria-current='page'] {
		color: var(--v3-text);
		background: var(--v3-surface);
	}
	.changelog-link {
		font-size: 0.78rem;
		font-weight: 600;
		color: var(--v3-muted);
		padding: 0.25rem 0.4rem;
		border-radius: 0.5rem;
		text-decoration: none;
	}
	.changelog-link:hover {
		color: var(--v3-text);
	}
	.theme-toggle {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 34px;
		height: 34px;
		border-radius: 9999px;
		background: transparent;
		border: 1px solid var(--v3-border);
		color: var(--v3-muted);
		cursor: pointer;
		transition:
			color 0.2s ease,
			border-color 0.2s ease;
	}
	.theme-toggle:hover {
		color: var(--v3-text);
		border-color: var(--v3-border-strong);
	}
</style>
